import React, { useMemo, useState } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import Select from '../../components/Select';
import fuzzySearch, { FuzzySearchIndex } from '../../utils/search/fuzzySearch';
import type { FuzzySearchResult } from '../../utils/search/fuzzySearch';
import useSeriesPolicyCheck, {
  SeriesPolicyAction,
} from '../useSeriesPolicyCheck';
import SeriesOption, { SERIES_MATCH_KEY } from './SeriesOption';
import * as styles from './SeriesSelect.module.scss';
import SeriesSelectValue from './SeriesSelectValue';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

const SERIES_SELECT_SERIES_FRAGMENT = gql(`
  fragment SeriesSelect_series on Series {
    id
    name
    ...useSeriesPolicyCheck_series
  }
`);

type SeriesFragment = DocumentType<typeof SERIES_SELECT_SERIES_FRAGMENT>;

interface SeriesSelectProps<Series extends SeriesFragment> {
  series: Series | null;
  setSeries(series: Series): void;
  options: Series[];
}

const SeriesSelect = <Series extends SeriesFragment>({
  series,
  setSeries,
  options,
}: SeriesSelectProps<Series>) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const seriesPolicyCheck = useSeriesPolicyCheck();

  const [inputValue, setInputValue] = useState('');

  const index = useMemo(
    () => new FuzzySearchIndex(options, [SERIES_MATCH_KEY]),
    [options],
  );
  const filtered: Pick<FuzzySearchResult<Series>, 'matches' | 'item'>[] =
    inputValue
      ? fuzzySearch(inputValue, index)
      : options.map((item) => ({ item, matches: [] }));

  return (
    <Select<{ series: Series; isDisabled: boolean }, false, false>
      autoFocus
      blurInputOnSelect
      className={styles.select}
      openMenuOnClick
      simpleValue={false}
      components={{
        SingleValue: SeriesSelectValue,
        Option: SeriesOption,
      }}
      getOptionLabel={({ series: { name } }) => name}
      getOptionValue={({ series: { id } }) => id}
      filterOption={null}
      inputValue={inputValue}
      onInputChange={(newValue) => setInputValue(newValue)}
      value={series ? { series, isDisabled: false } : null}
      onChange={(option) => {
        if (option) {
          setSeries(option.series);
        }
      }}
      options={filtered.map((option) => ({
        matches: option.matches,
        series: option.item,
        isDisabled: !seriesPolicyCheck(
          SeriesPolicyAction.EditSeries,
          option.item,
          currentUser,
        ),
      }))}
    />
  );
};

export default SeriesSelect;
